import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LABEL, RELEASES } from "@/data/catalog";
import { usePlayer } from "@/context/PlayerContext";
import { Play, ArrowDown } from "lucide-react";

const HERO_TEXTURE =
  "https://images.unsplash.com/photo-1556388275-bb5585725aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxzbW9rZSUyMHRleHR1cmUlMjBibGFjayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg2MzA0NzU0fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["SMOKE", "SHOP", "RECORDS"];

const lineV = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.76, 0, 0.24, 1] } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { play } = usePlayer();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-void">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_TEXTURE} alt="" className="h-full w-full object-cover opacity-40 mix-blend-screen" />
        <div className="absolute inset-0 vignette" />
        <div className="absolute inset-0 bg-void/40" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 h-full flex flex-col justify-center px-5 md:px-10 max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-4 mb-6 md:mb-10"
        >
          <span className="h-px w-10 md:w-16 bg-signal" />
          <span className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-white/60">
            EST. {LABEL.est} — {LABEL.tagline}
          </span>
        </motion.div>

        <h1 className="font-display uppercase leading-[0.82] tracking-tighter text-white text-[19vw] md:text-[15vw] lg:text-[13vw]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineV}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 2 ? <span className="text-stroke-red">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8"
        >
          <button
            data-testid="hero-play-latest"
            onClick={() => play(RELEASES[0])}
            className="group flex items-center gap-4 bg-signal text-void pl-5 pr-7 py-4 hover:bg-white transition-colors"
          >
            <span className="grid place-items-center h-9 w-9 rounded-full bg-void text-signal group-hover:text-void group-hover:bg-void/10 transition-colors">
              <Play size={16} className="fill-current ml-0.5" />
            </span>
            <span className="font-mono uppercase tracking-[0.2em] text-xs">Play the drop</span>
          </button>
          <p className="font-body text-sm text-white/50 max-w-xs">
            {RELEASES.length}+ releases in rotation. Streaming across the underground, one loud record at a time.
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-5 md:right-10 z-10 flex items-center gap-3 text-white/40"
      >
        <span className="font-mono uppercase tracking-[0.25em] text-[10px]">Scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
